import {
  ChevronLeft,
  EyeOff,
  ImagePlus,
  Trash01,
  Users02,
} from "@untitled-ui/icons-react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import Button from "@/components/forms/Button";
import Field from "@/components/forms/Field";
import Input from "@/components/forms/Input";
import Label from "@/components/forms/Label";
import RadioGroup from "@/components/forms/RadioGroup";
import DropZone from "@/components/helpers/DropZone";
import Loading from "@/components/loadings/Loading";
import UserAvatar from "@/components/media/UserAvatar";
import type { User } from "@/types";

import MessengerCard from "../base/MessengerCard";
import GroupPrivacyRadio from "./GroupPrivacyRadio";
import type { GroupPrivacy } from "./types";

type SettingsPageProps = {
  groupName: string;
  privacy: GroupPrivacy;
  selectedUsers: User<true>[];
  imagePreview?: string;
  isImageUploading?: boolean;
  isSubmitting?: boolean;
  onBack: () => void;
  onGroupNameChange: (value: string) => void;
  onPrivacyChange: (value: GroupPrivacy) => void;
  onImageUpload: (file: File) => void;
  onImageDelete: () => void;
  groupNameError?: string;
};

export default function SettingsPage({
  groupName,
  privacy,
  selectedUsers,
  imagePreview,
  isImageUploading,
  isSubmitting,
  onBack,
  onGroupNameChange,
  onPrivacyChange,
  onImageUpload,
  onImageDelete,
  groupNameError,
}: SettingsPageProps) {
  const { t } = useTranslation("messenger.user");

  const centerPositionClass =
    "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2";

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="px-4">
        <div className="flex items-center py-4">
          <Button
            color="tertiary-gray"
            onClick={onBack}
            disabled={isSubmitting}
          >
            <ChevronLeft className="size-5 text-gray-700 rtl:rotate-180" />
          </Button>

          <span className="text-lg font-semibold text-gray-900">
            {t("Back", { ns: "core.shared" })}
          </span>
        </div>

        <DropZone
          accept={["image/*"]}
          hasBorder={false}
          preview={imagePreview}
          onUpload={onImageUpload}
          onDeleted={onImageDelete}
        >
          <div className="flex justify-center pb-4">
            <div
              className={classNames(
                "group relative size-24",
                !imagePreview && !isImageUploading && "cursor-pointer",
              )}
            >
              <div
                className={classNames(
                  "flex size-full items-center justify-center overflow-hidden rounded-full bg-gray-100 transition-all",
                  imagePreview && "group-hover:blur-xs",
                )}
              >
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt={t("Group Image")}
                    className="size-full object-cover"
                  />
                ) : (
                  <ImagePlus className="size-7 text-gray-500" />
                )}
              </div>

              {isImageUploading ? (
                <Loading className={centerPositionClass} />
              ) : (
                imagePreview && (
                  <button
                    type="button"
                    className={classNames(
                      centerPositionClass,
                      "hidden size-8 cursor-pointer group-hover:block",
                    )}
                    onClick={(event) => {
                      event.stopPropagation();
                      onImageDelete();
                    }}
                  >
                    <Trash01 className="text-error-600 size-8" />
                  </button>
                )
              )}
            </div>
          </div>
        </DropZone>

        <Field>
          <Label required>{t("Group Name")}</Label>

          <Input
            placeholder={t("What do you want to name it?")}
            value={groupName}
            onChange={(event) => onGroupNameChange(event.target.value)}
            error={groupNameError}
            disabled={isSubmitting}
          />
        </Field>

        <div className="flex flex-col gap-3 py-4">
          <RadioGroup
            value={privacy}
            onChange={(value) => onPrivacyChange(value)}
            direction="vertical"
            className="gap-3"
          >
            <GroupPrivacyRadio
              value="public"
              title={t("Public Group")}
              description={t("All users can view or join this group.")}
              icon={<Users02 className="size-4" />}
            />

            <GroupPrivacyRadio
              value="private"
              title={t("Private Group")}
              description={t("Only invited users can view or join this group.")}
              icon={<EyeOff className="size-4" />}
            />
          </RadioGroup>
        </div>
      </div>

      <div className="bg-gray-50 px-10 py-2 text-sm/5 font-medium text-gray-600">
        {t("Group Members", {
          count: selectedUsers.length,
        })}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pt-2">
        {selectedUsers.map((user) => (
          <MessengerCard
            key={user.id}
            image={<UserAvatar size="lg" user={user} disableTooltip />}
            title={`${user.first_name} ${user.last_name}`}
            subtitle={user.position?.name || "-"}
            onClick={() => undefined}
          />
        ))}
      </div>
    </div>
  );
}
